'use client';

import type { GA4Item } from '@lib/analytics/types';
import { useNavigationLoading } from '@lib/hooks/use-navigation-loading';
import {
  buildImagePreloadHref,
  buildImagePreloadSrcSet,
  PRODUCT_THUMBNAIL_SIZES,
  THUMBNAIL_IMAGE_QUALITY,
  THUMBNAIL_WIDTHS,
} from '@lib/util/image-preload';
import { buildPlpLinkParams } from '@lib/util/pagination';
import type { SortOptions } from '@lib/util/sort-products';
import { TrackItemList } from '@modules/analytics/components/trackers';
import { Spinner } from '@modules/common/icons';
import PlpLiveStatus from '@modules/store/components/plp-live-status';
import PlpPagination from '@modules/store/components/plp-pagination';
import ProductGrid from '@modules/store/components/product-grid';
import { PRODUCTS_PAGE_SIZE } from '@modules/store/lib/product-query';
import dynamic from 'next/dynamic';
import type { ProductCardVM } from 'types/view-models';

const ProductToolbar = dynamic(
  () => import('@modules/store/components/product-toolbar'),
);

const SCROLL_TARGET_ID = 'products-list';

type PlpResultsSectionProps = {
  cards: ProductCardVM[];
  ga4Items: GA4Item[];
  sort: SortOptions;
  page: number;
  /** Total matching count — drives both the results label and the pager. */
  count: number;
  pathname: string;
  minPrice?: number;
  maxPrice?: number;
  searchQuery?: string;
  listId?: string;
  listName?: string;
};

/** Toolbar + grid + pager for one already-fetched page of a server-rendered
 * PLP result — shared by `ServerProducts`, `PopularitySortedProducts`,
 * `PriceSortedProducts`, and `ProductGridResult` (search and the dynamic
 * categories/collections routes alike) to avoid re-deriving the same
 * pagination-params/markup in each. */
export default function PlpResultsSection({
  cards,
  ga4Items,
  sort,
  page,
  count,
  pathname,
  minPrice,
  maxPrice,
  searchQuery,
  listId,
  listName,
}: PlpResultsSectionProps) {
  const { navigate, isPending } = useNavigationLoading();

  const paginationParams = buildPlpLinkParams({
    sortBy: sort,
    minPrice,
    maxPrice,
    searchQuery,
  });

  // LCP preload for the first product thumbnail. React hoists this <link>
  // into <head> during SSR, so the dynamic (page ≥ 2 / sorted / filtered)
  // PLP routes get the same hint the static page-1 routes emit at page
  // level. React dedupes float hints by href, so /search's own early shell
  // preload doesn't double up.
  const firstThumb = cards[0]?.thumbnail;

  return (
    <div id={SCROLL_TARGET_ID}>
      {firstThumb && (
        <link
          rel="preload"
          as="image"
          href={buildImagePreloadHref(firstThumb, 384, THUMBNAIL_IMAGE_QUALITY)}
          imageSrcSet={buildImagePreloadSrcSet(
            firstThumb,
            THUMBNAIL_WIDTHS,
            THUMBNAIL_IMAGE_QUALITY,
          )}
          imageSizes={PRODUCT_THUMBNAIL_SIZES}
          fetchPriority="high"
        />
      )}
      <PlpLiveStatus
        isLoading={isPending}
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={count}
      />
      <TrackItemList items={ga4Items} listId={listId} listName={listName} />
      <ProductToolbar
        sortBy={sort}
        productCount={count}
        minPrice={minPrice}
        maxPrice={maxPrice}
        navigate={navigate}
      />
      {isPending ? (
        // Announcements come from the persistent PlpLiveStatus region above —
        // the spinner itself is purely visual.
        <div className="flex justify-center py-40" aria-hidden="true">
          <Spinner
            size={48}
            className="text-fg-interactive-strong animate-spin motion-reduce:animate-none"
          />
        </div>
      ) : (
        <ProductGrid products={cards} page={page} />
      )}
      <PlpPagination
        pathname={pathname}
        currentParams={paginationParams}
        page={page}
        pageSize={PRODUCTS_PAGE_SIZE}
        totalItems={count}
        navigate={navigate}
      />
    </div>
  );
}
